import React, { ChangeEvent } from 'react';
import * as Yup from 'yup';
import { FormikField, ZenForm, ZenFormItem } from '../../../components/form';
import Dialog, { DialogProps } from '../../../components/dialog/Dialog';
import config from '../../../config/config';
import { Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Formik, FormikHelpers } from 'formik';
import { usernameForLinkRegex } from '../../../shared/utils/string';
import { ApiError } from '../../../shared/types/api-response';
import { useSiteEditDesignInfoMutation } from '../../../generated/types';
import { enqueueSnackbar } from 'notistack';

interface EditSiteUrlModalProps extends Omit<DialogProps, 'onClose'> {
    username: string;
    onClose: () => void;
    onSuccess?: () => void;
}

export interface UsernameValues {
    username: string;
}

const usernameSchema = () =>
    Yup.object().shape({
        username: Yup.string()
            .required('URL is required.')
            .matches(usernameForLinkRegex, 'Please only use alphanumerical, minus, and underscore.')
            .max(30, 'Please use a maximum of 30 characters.'),
    });

const EditSiteUrlModal: React.FC<EditSiteUrlModalProps> = ({ username, onClose, onSuccess, ...props }) => {
    const [editSiteInfo] = useSiteEditDesignInfoMutation();

    const handleUpdateSiteURL = async (values: UsernameValues, formik: FormikHelpers<UsernameValues>) => {
        await editSiteInfo({
            variables: {
                input: {
                    path: values.username,
                },
            },
            onError: (e: any) => {
                const err = e as ApiError;
                if (err.status === 400) {
                    formik.setFieldError('username', 'This URL is already taken');
                } else if (err.status === 500) {
                    enqueueSnackbar(`Can not edit site URL. Try again later!`, {
                        variant: 'error',
                    });
                } else {
                    formik.setFieldError('username', 'Can not publish design!');
                }
            },
            onCompleted() {
                onSuccess && onSuccess();
            },
        });
    };

    return (
        <Dialog {...props} onClose={onClose}>
            <div className="w-full pt-20 px-30 pb-40">
                <Formik
                    initialValues={{
                        username: username || '',
                    }}
                    validationSchema={usernameSchema}
                    enableReinitialize
                    onSubmit={(values, formikHelpers) =>
                        handleUpdateSiteURL(values, formikHelpers as FormikHelpers<UsernameValues>)
                    }
                >
                    {({ handleSubmit, isSubmitting, setFieldValue }) => (
                        <ZenForm type={`vertical`} className={`mt-20 px-30 pb-40`} onSubmit={handleSubmit}>
                            <div className="mt-20 flex-shrink-0">
                                <ZenFormItem>
                                    <FormikField
                                        as={`text`}
                                        name={`username`}
                                        noPadding
                                        placeholder={`www.mybusiness.com`}
                                        size={`sm`}
                                        className={`rounded-6`}
                                        prefix={
                                            <div className="whitespace-nowrap overflow-hidden pl-12 pr-10 font-semibold text-14 text-primary-50">
                                                {`${config.liveBioDomain}/`}
                                            </div>
                                        }
                                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                            setFieldValue('username', e.target.value.toLowerCase())
                                        }
                                    />
                                </ZenFormItem>
                                <div className="flex items-center mt-30 -mx-5">
                                    <div className={`flex-1 px-5`}>
                                        <Button
                                            size={`large`}
                                            color={`primary`}
                                            variant="outlined"
                                            fullWidth={true}
                                            onClick={onClose}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                    <div className={`flex-1 px-5`}>
                                        <LoadingButton
                                            loading={isSubmitting}
                                            size={`large`}
                                            color={`primary`}
                                            variant={'contained'}
                                            fullWidth={true}
                                            type={`submit`}
                                        >
                                            Update
                                        </LoadingButton>
                                    </div>
                                </div>
                            </div>
                        </ZenForm>
                    )}
                </Formik>
            </div>
        </Dialog>
    );
};

export default EditSiteUrlModal;
